import React, { useState, Fragment } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { CheckoutHeader } from '../components/layout/CheckoutHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { CheckoutFooter } from '../components/layout/CheckoutFooter';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { useScreenInit } from '../useScreenInit.js';
import { CouponModal } from '../components/modals/CouponModal';
import { LocationModal } from '../components/modals/LocationModal';
import { LoyaltyPointsSection } from '../components/checkout/LoyaltyPointsSection';
import { WalletBalance } from '../components/checkout/WalletBalance';
import { PrescriptionUploadSidebar } from '../components/prescription/PrescriptionUploadSidebar';
import { PaymentProcessingBottomSheet } from '../components/checkout/PaymentProcessingBottomSheet';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, EditIcon, TruckIcon, TagIcon, PackageIcon, ZapIcon, SnowflakeIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, AlertCircleIcon, HomeIcon, ArrowLeftIcon } from 'lucide-react';
export function CheckoutPage() {
  const navigate = useNavigate();
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('same-day');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const screenInit = useScreenInit();
  const [currentMobileStep, setCurrentMobileStep] = useState<'shipping' | 'payment' | 'complete'>(screenInit.currentMobileStep || 'shipping');
  const [deliveryAddress, setDeliveryAddress] = useState({
    type: 'Home',
    address: 'Q0851+3GR, Al Quds, Riyadh 13214, Saudi Arabia, Riyadh, SA',
    name: 'Ahmed',
    phone: '530151938'
  });
  const [appliedLoyaltyPoints, setAppliedLoyaltyPoints] = useState<{
    program: string;
    amount: number;
  } | null>(null);
  const [walletBalance] = useState(250.0);
  const [appliedWalletBalance, setAppliedWalletBalance] = useState(0);
  const [paymentProcessingOpen, setPaymentProcessingOpen] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [isMobileSummaryExpanded, setIsMobileSummaryExpanded] = useState(false);
  const orderItems = [{
    id: 1,
    name: 'Dyson Corrale Hair Straightener Prussian Blue',
    price: 1999,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=400&h=400&fit=crop'
  }, {
    id: 2,
    name: 'FREE - Beamarry Argan Smooth Conditioner 380 ML',
    price: 54.12,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=400&h=400&fit=crop'
  }];
  const shippingMethods = [{
    id: 'same-day',
    name: 'Same Day Delivery',
    description: 'Get it today',
    price: 0,
    icon: <ZapIcon className="w-5 h-5" data-id="element-6605" data-mp="2jc7dow3ncafo" />,
    badge: 'Get it today',
    details: 'AC Controlled Delivery / Not Chilled'
  }, {
    id: 'standard',
    name: 'Standard Delivery',
    description: '3-5 business days',
    price: 0,
    icon: <PackageIcon className="w-5 h-5" data-id="element-6606" data-mp="1qousap9828ny" />
  }, {
    id: 'express-120',
    name: 'Express Delivery - 120 Minutes',
    description: 'Same day delivery within 2 hours',
    price: 15,
    icon: <ZapIcon className="w-5 h-5" data-id="element-6607" data-mp="1zmn4hkjnd074" />
  }, {
    id: 'express-refrigerated',
    name: 'Express Refrigerated Delivery',
    description: 'Temperature controlled delivery',
    price: 0,
    icon: <SnowflakeIcon className="w-5 h-5" data-id="element-6608" data-mp="3b57rc5sb6xz0" />
  }, {
    id: 'express',
    name: 'Express Delivery',
    description: '1-2 business days',
    price: 25,
    icon: <TruckIcon className="w-5 h-5" data-id="element-6609" data-mp="f7s3gbju6fhe" />
  }];
  const paymentMethods = [{
    id: 'cash',
    name: 'Cash On Delivery',
    icon: '💵'
  }, {
    id: 'mada',
    name: 'Mada',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/mada.svg?v=2.1.18'
  }, {
    id: 'credit',
    name: 'Credit Card',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/visa.svg?v=2.1.18'
  }, {
    id: 'mastercard',
    name: 'Mastercard',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/master-card.svg?v=2.1.18'
  }, {
    id: 'stc',
    name: 'STC Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/stc-pay.svg?v=2.1.18'
  }, {
    id: 'apple',
    name: 'Apple Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/apple-pay.svg?v=2.1.18'
  }, {
    id: 'tamara',
    name: 'Tamara',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/tamara.svg?v=2.1.18'
  }];
  // Payment method colors mapping
  const paymentMethodColors = {
    cash: 'bg-green-600 hover:bg-green-700',
    mada: 'bg-[#00A8E1] hover:bg-[#0090c5]',
    credit: 'bg-blue-600 hover:bg-blue-700',
    mastercard: 'bg-[#EB001B] hover:bg-[#cc0017]',
    stc: 'bg-purple-600 hover:bg-purple-700',
    apple: 'bg-black hover:bg-gray-900',
    tamara: 'bg-[#1a1a1a] hover:bg-black'
  };
  const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discount = 451.86;
  const selectedShipping = shippingMethods.find(m => m.id === selectedDeliveryMethod);
  const shippingCost = selectedShipping?.price || 0;
  const tax = 76.25;
  const loyaltyDiscount = appliedLoyaltyPoints?.amount || 0;
  const walletDiscount = appliedWalletBalance;
  const totalDiscounts = discount + loyaltyDiscount + walletDiscount;
  const total = subtotal - totalDiscounts + shippingCost + tax;
  const installmentOptions = [{
    provider: 'Tamara',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Tamara_Logo.svg/2560px-Tamara_Logo.svg.png',
    installments: 12,
    amount: 133,
    color: 'bg-[#1a1a1a]'
  }, {
    provider: 'Tabby',
    logo: 'https://tabby.ai/assets/images/tabby-logo.svg',
    installments: 4,
    amount: 400,
    color: 'bg-[#3EDDB4]'
  }, {
    provider: 'MADA',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/64/Mada_Logo.svg/2560px-Mada_Logo.svg.png',
    installments: 4,
    amount: 400,
    color: 'bg-[#00A8E1]'
  }, {
    provider: 'Modlu',
    logo: 'https://modlu.com/assets/images/logo.svg',
    installments: 6,
    amount: 267,
    color: 'bg-[#5B4FFF]'
  }];
  const applyCoupon = () => {
    console.log('Applying coupon:', couponCode);
  };
  const handleLocationSelect = (location: any) => {
    setDeliveryAddress(location);
    setSelectedLocation(location);
    setLocationModalOpen(false);
  };
  const handleShippingMethodChange = (methodId: string) => {
    setSelectedDeliveryMethod(methodId);
    setCurrentMobileStep('payment');
  };
  const handlePaymentMethodSelect = (methodId: string) => {
    setSelectedPaymentMethod(methodId);
    setCurrentMobileStep('complete');
  };
  const handleCheckout = () => {
    if (window.innerWidth < 1024) {
      setPaymentProcessingOpen(true);
    } else {
      console.log('Processing checkout...');
    }
  };
  const handleLoyaltyPointsApplied = (program: string, amount: number) => {
    setAppliedLoyaltyPoints({
      program,
      amount
    });
  };
  const handleWalletBalanceApplied = (amount: number) => {
    setAppliedWalletBalance(amount);
  };
  const handleWalletBalanceRemoved = () => {
    setAppliedWalletBalance(0);
  };
  const getProgramName = (programId: string) => {
    const programs = {
      tamayouz: 'Tamayouz Points',
      qitaf: 'Qitaf Points',
      mokafaa: 'Mokafaa Points'
    };
    return programs[programId] || 'Loyalty Points';
  };
  const getMobileButtonContent = () => {
    if (currentMobileStep === 'shipping') {
      return {
        text: 'Select Delivery Method',
        disabled: false,
        bgColor: 'bg-[#1E2B55]',
        icon: null
      };
    } else if (currentMobileStep === 'payment') {
      return {
        text: 'Select Payment Method',
        disabled: false,
        bgColor: 'bg-[#1E2B55]',
        icon: null
      };
    } else {
      const paymentMethod = paymentMethods.find(m => m.id === selectedPaymentMethod);
      return {
        text: `Pay with ${paymentMethod?.name || 'Selected Method'}`,
        disabled: false,
        bgColor: paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]',
        icon: paymentMethod?.logo || null
      };
    }
  };
  const handleMobileCheckout = () => {
    if (currentMobileStep === 'shipping') {
      document.getElementById('shipping-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    } else if (currentMobileStep === 'payment') {
      document.getElementById('payment-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      handleCheckout();
    }
  };
  const mobileButton = getMobileButtonContent();
  const handlePrescriptionUpload = (file: File) => {
    console.log('Prescription uploaded:', file.name);
  };
  const handleConsultDoctor = () => {
    console.log('Consult doctor clicked');
  };
  const handlePaymentSuccess = () => {
    setPaymentProcessingOpen(false);
    navigate('/thank-you');
  };
  const handlePaymentCancel = () => {
    setPaymentProcessingOpen(false);
  };
  const handlePaymentFailure = (reason: string) => {
    setPaymentProcessingOpen(false);
    setPaymentError(reason);
    setTimeout(() => {
      document.getElementById('payment-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  return <HelmetProvider data-id="element-6610" data-mp="17wqj0wlxi0sp">
      <SEO title="Checkout" description="Complete your order at Innova Pharmacy with secure payment options and fast delivery across Saudi Arabia." canonical="/en/checkout/" hreflangAr="/checkout/" hreflangEn="/en/checkout/" hreflangDefault="/checkout/" data-id="element-6611" data-mp="17w79o4acgkqa" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6612" data-mp="1cwv28ue819mm">
        {/* Simplified Header for Checkout */}
        <div className="hidden md:block" data-id="element-6613" data-mp="p70qyh9jvt4h">
          <CheckoutHeader data-id="element-6614" data-mp="p3wdeta73l15" />
        </div>
        <MobileInternalHeader title="Checkout" showActions={false} data-id="element-6615" data-mp="k7xlwwtklmwi" />

        <main className="flex-grow pt-14 md:pt-16 pb-32 md:pb-0" data-id="element-6616" data-mp="yw284aru0gk1">
          <h1 className="sr-only" data-id="element-6617" data-mp="yxd962tw7a7g">Checkout</h1>

          <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 max-w-7xl" data-id="element-6618" data-mp="u4tu11fk470j">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 sm:mb-8" data-id="element-6619" data-mp="6fhpwfz36p53">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold" data-id="element-6620" data-mp="1h6c5u5o7uhl">
                Checkout
              </h1>

              {/* Desktop Stepper - Better Design */}
              <div className="hidden lg:flex items-center gap-4 min-w-[400px]" data-id="element-6621" data-mp="2huqbcyx92qzz">
                {[{
                key: 'shipping',
                label: 'Delivery',
                step: 1
              }, {
                key: 'payment',
                label: 'Payment',
                step: 2
              }, {
                key: 'complete',
                label: 'Review',
                step: 3
              }].map((s, idx) => {
                const order = {
                  shipping: 0,
                  payment: 1,
                  complete: 2
                } as const;
                const currentIdx = order[currentMobileStep];
                const isCompleted = idx < currentIdx;
                const isActive = idx === currentIdx;
                return <Fragment key={s.key} data-id="element-6622" data-mp="3i1vebo0141hl" data-mpi={idx}>
                      <div className="flex items-center gap-3" data-id="element-6623" data-mp="3i5128zwx8xbq">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300
                          ${isCompleted ? 'bg-emerald-500 text-white' : isActive ? 'bg-[#1E2B55] text-white ring-4 ring-[#1E2B55]/10' : 'bg-gray-100 text-gray-400'}
                        `} data-id="element-6624" data-mp="3d54z12w1t9x8">
                          {isCompleted ? <CheckIcon className="w-4 h-4" data-id="element-6625" data-mp="2uff7conl149j" /> : s.step}
                        </div>
                        <span className={`text-sm font-semibold ${isActive ? 'text-[#1E2B55]' : isCompleted ? 'text-emerald-600' : 'text-gray-400'}`} data-id="element-6626" data-mp="2leh0lylrein9">
                          {s.label}
                        </span>
                      </div>
                      {idx < 2 && <div className="h-0.5 flex-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-6627" data-mp="26i0pvr72gytq">
                          <motion.div className="h-full bg-emerald-500" initial={{
                      width: 0
                    }} animate={{
                      width: isCompleted ? '100%' : '0%'
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-6628" data-mp="2b8pgl4ax9rrt" />
                        </div>}
                    </Fragment>;
              })}
              </div>
            </div>

            {/* Payment Error Alert - Mobile */}
            {paymentError && <div className="lg:hidden mb-4 bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-6629" data-mp="37y6v88rvuo5n">
                <div className="flex items-start gap-3" data-id="element-6630" data-mp="36h16u368nfab">
                  <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-6631" data-mp="36jnpoyykxh7u" />
                  <div data-id="element-6632" data-mp="31kubf4bgxm6m">
                    <h3 className="font-bold text-red-900 mb-1" data-id="element-6633" data-mp="3fiiss26bub6x">
                      Payment Failed
                    </h3>
                    <p className="text-sm text-red-700" data-id="element-6634" data-mp="3bgntt4bvc9wb">{paymentError}</p>
                    <p className="text-xs text-red-600 mt-2" data-id="element-6635" data-mp="3pgf5gyewxe75">
                      Please try another payment method or contact support
                    </p>
                  </div>
                </div>
                <button onClick={() => setPaymentError(null)} className="mt-3 text-sm font-medium text-red-600 hover:text-red-700" data-id="element-6636" data-mp="30ueutx6r6hd">
                  Dismiss
                </button>
              </div>}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-6637" data-mp="bzpnsdrm6uod">
              {/* Main Content Area */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6" data-id="element-6638" data-mp="qohhzcxyrp5t">
                {/* Delivery Address Section */}
                <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6" data-id="element-6639" data-mp="vo3ufk5yx1dk">
                  <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4" data-id="element-6640" data-mp="hnt1mbl3lcpr">
                    Your Delivery Address
                  </h2>
                  <div className="flex items-start gap-3 sm:gap-4" data-id="element-6641" data-mp="lo37lo8w434m">
                    <div className="flex-shrink-0" data-id="element-6642" data-mp="7xhmdsvyrh3k">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-6643" data-mp="7womnbvf9rux">
                        <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#1E2B55]" data-id="element-6644" data-mp="3u57u89k52yfy" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-6645" data-mp="2zybn2yh5mgd">
                      <div className="flex items-center justify-between mb-2" data-id="element-6646" data-mp="2wk5djmowt3n">
                        <h3 className="font-bold text-sm sm:text-base" data-id="element-6647" data-mp="3l5b8afp4wdol">
                          {deliveryAddress.type}
                        </h3>
                        <button onClick={() => setLocationModalOpen(true)} className="text-[#1E2B55] hover:text-[#162041] flex-shrink-0" data-id="element-6648" data-mp="1vnxiwzbxabdc">
                          <EditIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-6649" data-mp="1lymfntbpw0xd" />
                        </button>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 mb-1 break-words" data-id="element-6650" data-mp="1hxu3ar7jtfwv">
                        {deliveryAddress.address}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600" data-id="element-6651" data-mp="29o8e2ek9mc4b">
                        {deliveryAddress.name}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600" data-id="element-6652" data-mp="25noez5g4syfj">
                        {deliveryAddress.phone}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Loyalty Points Section - NEW */}
                <LoyaltyPointsSection onPointsApplied={handleLoyaltyPointsApplied} data-id="element-6653" data-mp="p6ixd0azl96l" />
                {/* Wallet Balance Section - NEW */}
                <WalletBalance availableBalance={walletBalance} onBalanceApplied={handleWalletBalanceApplied} onBalanceRemoved={handleWalletBalanceRemoved} data-id="element-6654" data-mp="35t8ne4wlipza" />
                {/* Delivery Method Section */}
                <div id="shipping-section" className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6" data-id="element-6655" data-mp="3jl5eq6qfwvum">
                  <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4" data-id="element-6656" data-mp="3oiotr8uypcnm">
                    Please Select The Delivery Method
                  </h2>
                  <div className="space-y-3" data-id="element-6657" data-mp="1g27afd047r1">
                    {shippingMethods.map(method => <label key={method.id} className={`flex items-center justify-between p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedDeliveryMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-6658" data-mp="1z5tn8kp5uawj">
                        <div className="flex items-center flex-1" data-id="element-6659" data-mp="1fb23u5vwsmmd">
                          <input type="radio" name="shipping" value={method.id} checked={selectedDeliveryMethod === method.id} onChange={() => handleShippingMethodChange(method.id)} className="mr-3" data-id="element-6660" data-mp="1t38dd94k3vcu" />
                          <div className="flex items-center gap-2 sm:gap-3 flex-1" data-id="element-6661" data-mp="1aa4en9chesbd">
                            <div className={`p-2 rounded-lg ${selectedDeliveryMethod === method.id ? 'bg-[#1E2B55] text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-6662" data-mp="15c2xwv2j3532">
                              {method.icon}
                            </div>
                            <div className="flex-1" data-id="element-6663" data-mp="2p8a3p68xjv7l">
                              <div className="flex items-center gap-2 flex-wrap" data-id="element-6664" data-mp="2tbf1wwlz9aqf">
                                <p className="font-bold text-xs sm:text-sm" data-id="element-6665" data-mp="2fbnhad0ygw3g">
                                  {method.name}
                                </p>
                                {method.badge && <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-6666" data-mp="37zt1myo3kj2r">
                                    {method.badge}
                                  </span>}
                              </div>
                              <p className="text-xs text-gray-500" data-id="element-6667" data-mp="3392xforeqm9n">
                                {method.description}
                              </p>
                              {method.details && <p className="text-xs text-gray-400 mt-1" data-id="element-6668" data-mp="3vq5k33gyxy7l">
                                  {method.details}
                                </p>}
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-2 sm:ml-4 flex-shrink-0" data-id="element-6669" data-mp="1hfkciv0zl9d3">
                          <p className="font-bold text-sm sm:text-base text-green-600" data-id="element-6670" data-mp="1hdpym0o4uhii">
                            {method.price === 0 ? 'Free' : `SAR ${method.price.toFixed(2)}`}
                          </p>
                        </div>
                      </label>)}
                  </div>
                </div>
                {/* Payment Method Section */}
                <div id="payment-section" className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6" data-id="element-6671" data-mp="13ezqzr3oszfw">
                  <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4" data-id="element-6672" data-mp="2ssv41cfneu5t">
                    Select Payment Method
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-6673" data-mp="37wxxwhgs5vjh">
                    {paymentMethods.map(method => <button key={method.id} onClick={() => handlePaymentMethodSelect(method.id)} className={`border-2 rounded-lg p-4 flex sm:flex-col items-center sm:justify-center gap-3 sm:gap-0 hover:border-[#1E2B55] transition-colors min-h-[64px] sm:min-h-[100px] w-full ${selectedPaymentMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200'}`} data-id="element-6674" data-mp="32wtbkat6y6lc">
                        <div className="w-12 sm:w-auto flex justify-center sm:mb-2" data-id="element-6675" data-mp="3ize0ttfq99v">
                          {method.logo ? <img src={method.logo} alt={method.name} className="h-6 sm:h-8 object-contain max-w-full" data-id="element-6676" data-mp="3vlh48rg9ry8x" /> : <span className="text-2xl sm:text-3xl" data-id="element-6677" data-mp="4a7k9ozpis37">
                              {method.icon}
                            </span>}
                        </div>
                        <span className="text-sm font-medium text-left sm:text-center leading-tight flex-1 sm:flex-none" data-id="element-6678" data-mp="e59c4slda89h">
                          {method.name}
                        </span>
                        {selectedPaymentMethod === method.id && <div className="sm:hidden w-5 h-5 rounded-full bg-[#1E2B55] text-white flex items-center justify-center flex-shrink-0" data-id="element-6679" data-mp="s63n9izsunbu">
                            <CheckIcon className="w-3 h-3" data-id="element-6680" data-mp="wy3oderlvma0" />
                          </div>}
                      </button>)}
                  </div>
                  {/* Desktop Checkout Button - Below Payment Methods */}
                  <div className="hidden lg:block mt-6" data-id="element-6681" data-mp="1ay4q0wfp2xqj">
                    <button onClick={() => {
                    if (currentMobileStep === 'shipping') {
                      document.getElementById('shipping-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else if (currentMobileStep === 'payment') {
                      document.getElementById('payment-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else {
                      handleCheckout();
                    }
                  }} disabled={currentMobileStep === 'complete' && !selectedPaymentMethod} className={`w-full py-4 rounded-lg font-bold text-white transition-colors flex items-center justify-center gap-3 text-base ${currentMobileStep === 'complete' && !selectedPaymentMethod ? 'bg-gray-300 cursor-not-allowed' : currentMobileStep === 'complete' && selectedPaymentMethod ? paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]' : 'bg-[#1E2B55] hover:bg-[#162041]'}`} data-id="element-6682" data-mp="2vf5jan4hqlwo">
                      {currentMobileStep === 'shipping' && <>
                          <TruckIcon className="w-5 h-5" data-id="element-6683" data-mp="6i0u616dkip" />
                          Select Delivery Method
                        </>}
                      {currentMobileStep === 'payment' && <>
                          <TagIcon className="w-5 h-5" data-id="element-6684" data-mp="3rke1omd404r8" />
                          Select Payment Method
                        </>}
                      {currentMobileStep === 'complete' && selectedPaymentMethod && <>
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo} alt="" className="h-6 w-auto" data-id="element-6685" data-mp="23mmqo60bt9zm" /> : <span className="text-2xl" data-id="element-6686" data-mp="1ynjxbitpks7h">
                                {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
                              </span>}
                            Pay with{' '}
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
                          </>}
                    </button>
                  </div>
                </div>

                {/* Coupons Section - Moved here after Payment Methods */}
                <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6" data-id="element-6687" data-mp="1k0zsre6sdgyt">
                  <div className="flex items-center gap-2 mb-3" data-id="element-6688" data-mp="1o11vlqo9cgfo">
                    <TagIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" data-id="element-6689" data-mp="1nz7762ds266u" />
                    <div data-id="element-6690" data-mp="1szbsq6tjlr3x">
                      <p className="font-medium text-xs sm:text-sm" data-id="element-6691" data-mp="1xrcyd9w7gjkz">
                        Have a Coupon?
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-6692" data-mp="10cqzf0tib4xq">
                        Save more with promo codes
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mb-3" data-id="element-6693" data-mp="xs2sldnq620a">
                    <input type="text" value={couponCode} onChange={e => setCouponCode(e.target.value)} placeholder="Enter coupon code" className="flex-1 border border-gray-300 rounded-lg px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2B55]" data-id="element-6694" data-mp="xqrmv8apyk6j" />
                    <button onClick={applyCoupon} className="bg-[#1E2B55] text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium hover:bg-[#162041] transition-colors whitespace-nowrap" data-id="element-6695" data-mp="eui2k3984xe3">
                      Apply
                    </button>
                  </div>
                  <button onClick={() => setCouponModalOpen(true)} className="w-full text-xs sm:text-sm font-medium text-[#1E2B55] hover:underline text-center" data-id="element-6696" data-mp="3s7uznonq6xq9">
                    View Available Coupons
                  </button>
                </div>
              </div>
              {/* Checkout Sidebar - Desktop */}
              <div className="lg:col-span-1" data-id="element-6697" data-mp="15zdvgvnxn0ci">
                <div className="space-y-4 lg:sticky lg:top-24" data-id="element-6698" data-mp="15ykmdo1i3yr7">
                  {/* Prescription Upload - NEW */}
                  <PrescriptionUploadSidebar onPrescriptionUpload={handlePrescriptionUpload} onConsultDoctor={handleConsultDoctor} data-id="element-6699" data-mp="1a2sdtszcfst4" />
                  {/* Order Summary */}
                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6" data-id="element-6700" data-mp="mbenfskr67as">
                    {/* Order Totals */}
                    <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6" data-id="element-6701" data-mp="rb9cdqkywmt7">
                      <div className="flex justify-between text-xs sm:text-sm" data-id="element-6702" data-mp="d9wxlihjmr8w">
                        <span className="text-gray-600" data-id="element-6703" data-mp="i9jmip6qhvxj">Subtotal</span>
                        <span className="font-medium" data-id="element-6704" data-mp="i8qb3awtyi92">
                          SAR {subtotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm" data-id="element-6705" data-mp="3pv2nzrs0wy78">
                        <span className="text-gray-600" data-id="element-6706" data-mp="7oxrgs0qhn2x">Discount</span>
                        <span className="font-medium text-green-600" data-id="element-6707" data-mp="7m37t2pjo6ak">
                          -SAR {discount.toFixed(2)}
                        </span>
                      </div>
                      {appliedLoyaltyPoints && <div className="flex justify-between text-xs sm:text-sm" data-id="element-6708" data-mp="co0ygi09y3dj">
                          <span className="text-gray-600" data-id="element-6709" data-mp="rc5ipqez6myf">
                            {getProgramName(appliedLoyaltyPoints.program)}
                          </span>
                          <span className="font-medium text-green-600" data-id="element-6710" data-mp="me3wiwm967iq">
                            -SAR {appliedLoyaltyPoints.amount.toFixed(2)}
                          </span>
                        </div>}
                      {appliedWalletBalance > 0 && <div className="flex justify-between text-xs sm:text-sm" data-id="element-6711" data-mp="1xpophytdmiph">
                          <span className="text-gray-600" data-id="element-6712" data-mp="1ssf1mnlmyf3r">Wallet Balance</span>
                          <span className="font-medium text-green-600" data-id="element-6713" data-mp="1nr7tgnfkzdwk">
                            -SAR {appliedWalletBalance.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between text-xs sm:text-sm" data-id="element-6714" data-mp="1es48fdwusoff">
                        <span className="text-gray-600" data-id="element-6715" data-mp="1a0cdysaktyk0">
                          Shipping & Handling
                        </span>
                        <span className="font-medium text-green-600" data-id="element-6716" data-mp="151styxfjmwua">
                          {shippingCost === 0 ? 'Free' : `SAR ${shippingCost.toFixed(2)}`}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm" data-id="element-6717" data-mp="r2t7nwfi6u1a">
                        <span className="text-gray-600" data-id="element-6718" data-mp="32pv9b1fxa2tb">Tax</span>
                        <span className="font-medium" data-id="element-6719" data-mp="32rg1ikerl9o0">
                          SAR {tax.toFixed(2)}
                        </span>
                      </div>
                      <div className="border-t border-gray-200 pt-2 sm:pt-3 flex justify-between items-center" data-id="element-6720" data-mp="3br3439j8clin">
                        <span className="font-bold text-sm sm:text-base" data-id="element-6721" data-mp="3go3cvs7gs2dg">
                          Total (Include Vat)
                        </span>
                        <span className="text-lg sm:text-xl font-bold text-[#1E2B55]" data-id="element-6722" data-mp="2nzo25hvd867x">
                          SAR {total.toFixed(2)}
                        </span>
                      </div>
                      {(appliedLoyaltyPoints || appliedWalletBalance > 0) && <div className="pt-2 border-t border-gray-200" data-id="element-6723" data-mp="20amn3bs11wto">
                          <p className="text-xs text-green-600 font-medium" data-id="element-6724" data-mp="20acx3o6zfpnz">
                            You saved SAR{' '}
                            {(loyaltyDiscount + walletDiscount).toFixed(2)} with
                            points & wallet!
                          </p>
                        </div>}
                    </div>
                    {/* Desktop Complete Purchase Button */}
                    <button onClick={() => {
                    if (currentMobileStep === 'shipping') {
                      document.getElementById('shipping-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else if (currentMobileStep === 'payment') {
                      document.getElementById('payment-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else {
                      handleCheckout();
                    }
                  }} disabled={currentMobileStep === 'complete' && !selectedPaymentMethod} className={`hidden lg:flex w-full py-2.5 sm:py-3 rounded-lg font-bold text-white transition-colors mb-3 sm:mb-4 text-sm sm:text-base items-center justify-center gap-2 ${currentMobileStep === 'complete' && !selectedPaymentMethod ? 'bg-gray-300 cursor-not-allowed' : currentMobileStep === 'complete' && selectedPaymentMethod ? paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]' : 'bg-[#1E2B55] hover:bg-[#162041]'}`} data-id="element-6725" data-mp="270nxgi0oqi4l">
                      {currentMobileStep === 'shipping' && <>
                          <TruckIcon className="w-5 h-5 flex-shrink-0" data-id="element-6726" data-mp="1j3hv7j3rlu35" />
                          <span data-id="element-6727" data-mp="1j26slmm22sdm">Select Delivery Method</span>
                        </>}
                      {currentMobileStep === 'payment' && <>
                          <TagIcon className="w-5 h-5 flex-shrink-0" data-id="element-6728" data-mp="3vf2uegcvbnh3" />
                          <span data-id="element-6729" data-mp="3qdnawsqc9p0t">Select Payment Method</span>
                        </>}
                      {currentMobileStep === 'complete' && selectedPaymentMethod && <>
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo} alt="" className="h-6 w-auto flex-shrink-0" data-id="element-6730" data-mp="dxtc2i5sfi7f" /> : <span className="text-2xl flex-shrink-0" data-id="element-6731" data-mp="147k39kxl8s32">
                                {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
                              </span>}
                            <span data-id="element-6732" data-mp="1i4qjp8uuaep4">
                              Pay with{' '}
                              {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
                            </span>
                          </>}
                    </button>
                    {/* Free Delivery Banner */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4 flex items-start gap-2" data-id="element-6733" data-mp="25yh0mat97iw4">
                      <TruckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-6734" data-mp="athdpw68jfxu" />
                      <p className="text-xs sm:text-sm text-green-700 leading-tight" data-id="element-6735" data-mp="6s5ty7a1loxk">
                        <strong data-id="element-6736" data-mp="kjamkpeztgye">Congratulations!</strong> You have Unlocked Free
                        Delivery 🎉
                      </p>
                    </div>

                    {/* Order Summary Section - Mobile Only (Moved to bottom) */}
                    <div className="lg:hidden border-t border-gray-200 pt-4 mt-4 mb-4" data-id="element-6737" data-mp="ug5kp77ctfc5">
                      <h2 className="text-base font-bold mb-3" data-id="element-6738" data-mp="phc5nacf5g9v">
                        Order Summary
                      </h2>
                      <div className="space-y-3" data-id="element-6739" data-mp="13hdilo5yke5p">
                        {orderItems.map(item => <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0" data-id="element-6740" data-mp="yjv3th59f4ps">
                            <div className="flex-shrink-0 w-14 h-14 bg-gray-50 rounded-lg overflow-hidden" data-id="element-6741" data-mp="30c9kkcw2i34m">
                              <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6742" data-mp="1fuh6m6f7q1mz" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-6743" data-mp="1b0cy3iqatljs">
                              <h3 className="text-xs font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6744" data-mp="x26cp22jde10">
                                {item.name}
                              </h3>
                              <div className="flex items-center justify-between" data-id="element-6745" data-mp="11yn0fv5tpqdk">
                                <span className="text-xs text-gray-600" data-id="element-6746" data-mp="o3b4jkfxltv2">
                                  X {item.quantity}
                                </span>
                                <span className="text-sm font-bold text-gray-900" data-id="element-6747" data-mp="2igaqrwethmbb">
                                  SAR {item.price.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>

                    {/* Installment Options */}
                    <div className="space-y-2" data-id="element-6748" data-mp="10gd690ky46si">
                      {installmentOptions.map((option, index) => <div key={index} className="border border-gray-200 rounded-lg p-2 sm:p-3 flex items-center gap-2 sm:gap-3" data-id="element-6749" data-mp="vibgjwt0l982" data-mpi={index}>
                          <div className={`${option.color} rounded-lg p-1.5 sm:p-2 flex items-center justify-center w-10 sm:w-12 h-6 sm:h-8 flex-shrink-0`} data-id="element-6750" data-mp="mjzodqci23di">
                            <span className="text-white text-xs font-bold" data-id="element-6751" data-mp="2bwuu4cah913j">
                              {option.provider}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-6752" data-mp="26vxeo7guql5q">
                            <p className="text-xs sm:text-sm font-medium leading-tight" data-id="element-6753" data-mp="2fdzexl7e6tct">
                              {option.installments} Installments of SAR{' '}
                              {option.amount}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-6754" data-mp="2o9f4vr510od4">
                              Interest-Free
                            </p>
                          </div>
                          <button className="text-xs font-medium text-[#1E2B55] hover:underline whitespace-nowrap flex-shrink-0 hidden sm:block" data-id="element-6755" data-mp="1whpr466ouofm">
                            Learn More
                          </button>
                        </div>)}
                    </div>
                    {/* Order Items - Desktop Only */}
                    <div className="hidden lg:block mt-6 pt-6 border-t border-gray-200" data-id="element-6756" data-mp="3gw9lgcyq33lb">
                      <h3 className="text-base font-bold mb-4" data-id="element-6757" data-mp="3lukx8t8kvo66">Order Items</h3>
                      <div className="space-y-3" data-id="element-6758" data-mp="3lwnr5ocuq1g6">
                        {orderItems.map(item => <div key={item.id} className="flex gap-3" data-id="element-6759" data-mp="3n93cuw64l81j">
                            <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden" data-id="element-6760" data-mp="3n9d1ogffp95n">
                              <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6761" data-mp="3sa96cw7m0zc4" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-6762" data-mp="a18l8yqd4bw1">
                              <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6763" data-mp="etjituxmrllf">
                                {item.name}
                              </h4>
                              <div className="flex items-center justify-between" data-id="element-6764" data-mp="ju7amitoo6gp">
                                <span className="text-sm text-gray-600" data-id="element-6765" data-mp="opcvdebcnf37">
                                  X {item.quantity}
                                </span>
                                <span className="text-sm font-bold text-gray-900" data-id="element-6766" data-mp="2e9mfyb90xrz7">
                                  SAR {item.price.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <CheckoutFooter data-id="element-6767" data-mp="2s09jcgpqc61m" />
        {/* Mobile Complete Purchase Button - Fixed at bottom */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" data-id="element-6768" data-mp="2n8ztpg46l2pq">
          {/* Redesigned Stepper - Above Button */}
          <div className="flex items-center gap-2 mb-4 px-2" data-id="element-6769" data-mp="3fpaitegl46ur">
            {[{
            key: 'shipping',
            label: 'Delivery'
          }, {
            key: 'payment',
            label: 'Payment'
          }, {
            key: 'complete',
            label: 'Review'
          }].map((s, idx) => {
            const order = {
              shipping: 0,
              payment: 1,
              complete: 2
            } as const;
            const currentIdx = order[currentMobileStep];
            const isCompleted = idx < currentIdx;
            const isActive = idx === currentIdx;
            return <div key={s.key} className="flex-1 flex flex-col gap-1.5" data-id="element-6770" data-mp="udgtwem1v1y5" data-mpi={idx}>
                  <div className="flex items-center justify-between px-1" data-id="element-6771" data-mp="gd5xkw1z0v5l">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? 'text-[#1E2B55]' : isCompleted ? 'text-emerald-600' : 'text-gray-400'}`} data-id="element-6772" data-mp="lbpn16frwp2p">
                      {s.label}
                    </span>
                    {isCompleted && <CheckIcon className="w-3 h-3 text-emerald-600" data-id="element-6773" data-mp="4256lhsae12j" />}
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden relative" data-id="element-6774" data-mp="3v9hgdbqubc8f">
                    {(isActive || isCompleted) && <motion.div layoutId="step-fill" className={`absolute inset-0 rounded-full ${isCompleted ? 'bg-emerald-500' : 'bg-[#1E2B55]'}`} initial={{
                  x: '-100%'
                }} animate={{
                  x: 0
                }} transition={{
                  duration: 0.5,
                  ease: 'easeOut'
                }} data-id="element-6775" data-mp="duavgin8s2di" />}
                  </div>
                </div>;
          })}
          </div>

          {currentMobileStep === 'complete' ? <div className="flex items-center gap-4" data-id="element-6776" data-mp="1kmnvsrza4yxt">
              <div className="flex-1" data-id="element-6777" data-mp="1km5s5aaafifv">
                <p className="text-xs text-gray-500 font-medium" data-id="element-6778" data-mp="1pl8mo0sn669l">
                  Total Amount
                </p>
                <p className="text-lg font-bold text-[#1E2B55]" data-id="element-6779" data-mp="1uk1ylosa5p7n">
                  SAR {total.toFixed(2)}
                </p>
              </div>
              <button onClick={handleCheckout} disabled={!selectedPaymentMethod} className={`flex-1 py-3.5 rounded-lg font-bold text-white transition-colors text-base flex items-center justify-center gap-2 ${!selectedPaymentMethod ? 'bg-gray-300 cursor-not-allowed' : paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]'}`} data-id="element-6780" data-mp="1yldta8q0h0mp">
                {selectedPaymentMethod && <>
                    {paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo} alt="" className="h-5 w-auto" data-id="element-6781" data-mp="n5p55e10tvkb" /> : <span className="text-xl" data-id="element-6782" data-mp="1nzp1tunk6d4">
                        {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
                      </span>}
                    Pay Now
                  </>}
                {!selectedPaymentMethod && 'Select Payment'}
              </button>
            </div> : <button onClick={() => {
          if (currentMobileStep === 'shipping') {
            document.getElementById('shipping-section')?.scrollIntoView({
              behavior: 'smooth'
            });
          } else if (currentMobileStep === 'payment') {
            document.getElementById('payment-section')?.scrollIntoView({
              behavior: 'smooth'
            });
          }
        }} className="w-full py-3.5 rounded-lg font-bold text-white transition-colors text-base flex items-center justify-center gap-2 bg-[#1E2B55] hover:bg-[#162041]" data-id="element-6783" data-mp="3f0t5s2z5jqdd">
              {currentMobileStep === 'shipping' && <>
                  <TruckIcon className="w-5 h-5" data-id="element-6784" data-mp="3j5dg6bil23z3" />
                  Select Delivery Method
                </>}
              {currentMobileStep === 'payment' && <>
                  <TagIcon className="w-5 h-5" data-id="element-6785" data-mp="39fk65ey39wwy" />
                  Select Payment Method
                </>}
            </button>}
        </div>
        {/* Modals */}
        <CouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} onApplyCoupon={code => {
        setCouponCode(code);
        applyCoupon();
      }} data-id="element-6786" data-mp="5rs5sx41qwb7" />
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={handleLocationSelect} initialAddress={deliveryAddress} data-id="element-6787" data-mp="1lib856w6tuzy" />
        {/* Payment Processing Bottom Sheet - Mobile Only */}
        <PaymentProcessingBottomSheet isOpen={paymentProcessingOpen} onClose={() => setPaymentProcessingOpen(false)} paymentMethod={paymentMethods.find(m => m.id === selectedPaymentMethod)?.name || 'Selected Method'} onSuccess={handlePaymentSuccess} onCancel={handlePaymentCancel} onFailure={handlePaymentFailure} data-id="element-6788" data-mp="17k4hobtkixab" />
      </div>
    </HelmetProvider>;
}